import { create } from 'zustand';

/**
 * Zustand store for the unified chart building workflow.
 * Shared state across AI Assistant, Visual Builder, and SQL Editor tabs.
 */
export const useChartBuilderStore = create((set, get) => ({
  // Chart type - shared across all tabs
  chartType: 'bar',

  // Data source config
  sourceMode: 'auto', // 'auto' | 'single' | 'multi'
  selectedDataSource: 'auto',
  selectedDataSources: [],
  selectedSourceType: 'auto', // 'auto' | 'datasource' | 'view' | 'mart' | 'wideview'
  aiSelectedSource: null,
  aiSelectedSources: [],

  // Data source lists (fetched on modal open)
  dataSources: [],
  wideViews: [],
  marts: [],
  relationships: [],

  // Schema and table metadata
  schema: null,
  tableName: '',

  // Visual config (dimensions, measures, filters, sortBy, limit) - canonical for Visual Builder
  visualConfig: null,

  // SQL query - derived from visualConfig unless isCustomSQL
  sqlQuery: '',
  isCustomSQL: false,

  // Chart preview - from AI or query execution
  chartConfig: null, // { query, config: { xAxis, yAxis, series, kpiConfig }, ... }
  chartData: null,
  chartName: '',
  previewError: null,
  recommendedChartType: null,

  // Axis labels (optional)
  xAxisLabel: '',
  yAxisLabel: '',

  // Actions
  setChartType: (chartType) => set({ chartType }),

  setSourceConfig: (config) => set((state) => ({ ...state, ...config })),

  setDataSources: (dataSources, wideViews, marts, relationships) =>
    set({
      dataSources: dataSources || [],
      wideViews: wideViews || [],
      marts: marts || [],
      relationships: relationships || [],
    }),

  setSchema: (schema, tableName) => set({ schema, tableName: tableName || '' }),

  updateVisualConfig: (visualConfig, sqlQuery) =>
    set((state) => {
      const newVisualConfig = visualConfig ?? state.visualConfig;
      const newSqlQuery = sqlQuery ?? state.sqlQuery;
      const queryChanged = newSqlQuery !== state.sqlQuery;
      return {
        visualConfig: newVisualConfig,
        sqlQuery: newSqlQuery,
        isCustomSQL: false,
        chartData: queryChanged ? null : state.chartData,
        previewError: queryChanged ? null : state.previewError,
        chartConfig: state.chartConfig
          ? { ...state.chartConfig, query: newSqlQuery }
          : { query: newSqlQuery, config: {} },
      };
    }),

  setSqlQuery: (sqlQuery, isCustomSQL = true) => set({ sqlQuery, isCustomSQL }),

  ejectToCustomSQL: () => set({ isCustomSQL: true }),

  resetToVisual: (visualConfig, sqlQuery) =>
    set({ visualConfig, sqlQuery, isCustomSQL: false }),

  /**
   * Called when AI returns a chart suggestion. Writes all AI-generated state.
   */
  setFromAIResponse: (response) => {
    const {
      chartSuggestion,
      recommendedChartType,
      chartData: newChartData,
      selectedSource,
      selectedSources,
    } = response;

    const updates = { previewError: null };

    if (chartSuggestion) {
      updates.chartConfig = chartSuggestion;
      updates.sqlQuery = chartSuggestion.query || '';
      if (chartSuggestion.name) {
        updates.chartName = chartSuggestion.name;
      }
      if (chartSuggestion.config?.visualConfig) {
        updates.visualConfig = chartSuggestion.config.visualConfig;
        updates.isCustomSQL = false;
      } else if (chartSuggestion.query) {
        // AI returned raw SQL without visual config - treat as custom SQL
        updates.isCustomSQL = true;
      }
    }

    if (newChartData) {
      updates.chartData = newChartData;
    }

    if (recommendedChartType) {
      updates.chartType = recommendedChartType;
      updates.recommendedChartType = recommendedChartType;
    }

    if (selectedSource) {
      updates.aiSelectedSource = selectedSource;
    }
    if (selectedSources && selectedSources.length > 0) {
      updates.aiSelectedSources = selectedSources;
    }

    set(updates);
  },

  setPreviewData: (chartData, previewError = null) =>
    set({ chartData, previewError }),

  setChartName: (chartName) => set({ chartName }),

  setPreviewError: (previewError) => set({ previewError }),

  setAxisLabels: (xAxisLabel, yAxisLabel) => set({ xAxisLabel, yAxisLabel }),

  setChartConfig: (chartConfig) => set({ chartConfig }),

  setChartTypeWithConfig: (chartType) =>
    set((state) => ({
      chartType,
      chartConfig: state.chartConfig
        ? { ...state.chartConfig, type: chartType }
        : null,
    })),

  reset: () =>
    set({
      chartType: 'bar',
      sourceMode: 'auto',
      selectedDataSource: 'auto',
      selectedDataSources: [],
      selectedSourceType: 'auto',
      aiSelectedSource: null,
      aiSelectedSources: [],
      visualConfig: null,
      sqlQuery: '',
      isCustomSQL: false,
      chartConfig: null,
      chartData: null,
      chartName: '',
      previewError: null,
      recommendedChartType: null,
      xAxisLabel: '',
      yAxisLabel: '',
      schema: null,
      tableName: '',
    }),
}));
